import { CrunchServiceWithContext, CrunchAccountServiceWithContext } from "@crunchdao/sdk";
import { BaseCommand } from "@crunchdao/shared-cli";
import { formatAddressWithLink } from "@crunchdao/shared-cli";
import { createTable } from "@crunchdao/shared-cli";
import { isAccountDoesNotExistError, formatMicroUsdcToUsdc } from "@crunchdao/sdk";
import chalk from "chalk";
import { getAccount } from "@solana/spl-token";
import bs58 from "bs58";
import { PublicKey } from "@solana/web3.js";

export class GetCrunchCommand extends BaseCommand {
  private getCrunchService() {
    return CrunchServiceWithContext({
      program: this.program,
    });
  }

  private displayCrunchInfo(data: any) {
    // Header with crunch name
    console.log(chalk.cyan.bold(`\nCrunch: ${data.name}`));
    console.log(chalk.gray("═".repeat(80)));

    // Main info table
    const mainTable = createTable({ colWidths: [25, 55] });

    mainTable.push(
      [chalk.yellow("Address"), formatAddressWithLink(data.address, this.network)],
      [chalk.yellow("Status"), this.formatStatus(data.state)],
      [chalk.yellow("Total Payout"), chalk.green(data.payoutAmount)],
      [chalk.yellow("Participants"), chalk.white(data.participantCount.toString())],
      [
        chalk.yellow("Next Cruncher Index"),
        data.nextCruncherIndex !== null
          ? chalk.cyan(data.nextCruncherIndex.toString())
          : chalk.gray("Not initialized"),
      ],
      [chalk.yellow("Max Models/Cruncher"), chalk.white(data.maxModelsPerCruncher.toString())],
      [chalk.yellow("Active Models"), chalk.white(data.activeModelsCount.toString())],
      [chalk.yellow("Total Checkpoints"), chalk.white(data.checkpointsTotal.toString())],
      [chalk.yellow("Last Checkpoint"), chalk.white(`#${data.lastCheckpointIndex}`)]
    );

    console.log(mainTable.toString());

    // Current checkpoint section
    if (data.currentCheckpoint) {
      console.log(chalk.cyan.bold("\nCurrent Checkpoint"));
      console.log(chalk.gray("─".repeat(60)));

      const checkpointTable = createTable({ colWidths: [20, 60] });

      checkpointTable.push(
        [chalk.yellow("Index"), chalk.white(data.currentCheckpoint.index.toString())],
        [chalk.yellow("Status"), this.formatCheckpointStatus(data.currentCheckpoint.status)],
        [
          chalk.yellow("Created"),
          chalk.white(new Date(data.currentCheckpoint.timestamp).toLocaleString()),
        ],
        [chalk.yellow("Prizes Count"), chalk.white(data.currentCheckpoint.prizesCount.toString())],
        [
          chalk.yellow("Total Prizes"),
          chalk.green(formatMicroUsdcToUsdc(data.currentCheckpoint.totalPrizeAmount)),
        ],
        [
          chalk.yellow("Address"),
          formatAddressWithLink(data.currentCheckpoint.address, this.network),
        ]
      );

      console.log(checkpointTable.toString());
    }

    // Participants section
    if (data.participantCount > 0) {
      console.log(chalk.cyan.bold("\nParticipants"));
      console.log(chalk.gray("─".repeat(80)));

      const participantTable = createTable({ colWidths: [5, 8, 22, 30, 30, 10] });
      participantTable.push([
        chalk.yellow("#"),
        chalk.yellow("Index"),
        chalk.yellow("Name"),
        chalk.yellow("Cruncher Address"),
        chalk.yellow("Claim Record"),
        chalk.yellow("Models"),
      ]);

      if (data.participantsWithDetails) {
        // Sort participants: those with models first, then by index
        const sortedParticipants = [...data.participantsWithDetails].sort((a: any, b: any) => {
          if (a.modelCount > 0 && b.modelCount === 0) return -1;
          if (a.modelCount === 0 && b.modelCount > 0) return 1;
          return a.cruncherIndex - b.cruncherIndex;
        });

        sortedParticipants.forEach((participant: any, index: number) => {
          participantTable.push([
            chalk.white(index.toString()),
            chalk.cyan(
              participant.cruncherIndex !== undefined ? participant.cruncherIndex.toString() : "-"
            ),
            chalk.white(participant.name || "Unknown"),
            formatAddressWithLink(participant.address, this.network),
            formatAddressWithLink(participant.claimRecordAddress || "-", this.network),
            chalk.cyan(participant.modelCount.toString()),
          ]);

          // Show model details if available
          if (participant.models && participant.models.length > 0) {
            participant.models.forEach((model: any) => {
              participantTable.push([
                "",
                "",
                chalk.gray("  └─ " + (model.id || "Unknown")),
                chalk.gray(formatAddressWithLink(model.address, this.network)),
                "",
                chalk.gray(model.state || "Unknown"),
              ]);
            });
          }
        });
      } else {
        // Fallback to basic display
        data.participants.forEach((participant: string, index: number) => {
          participantTable.push([
            chalk.white(index.toString()),
            chalk.gray("-"),
            chalk.gray("Unknown"),
            formatAddressWithLink(participant, this.network),
            chalk.gray("-"),
            chalk.gray("0"),
          ]);
        });
      }

      if (data.participants.length > 10) {
        participantTable.push([
          chalk.gray("..."),
          "",
          chalk.gray(`and ${data.participants.length - 10} more`),
          "",
          "",
          "",
        ]);
      }

      console.log(participantTable.toString());
    }

    // Footer with vault info
    console.log(chalk.cyan.bold("\nReward Vault"));
    console.log(chalk.gray("─".repeat(40)));
    console.log(
      data.rewardVaultDisplay
        ? data.rewardVaultDisplay
        : formatAddressWithLink(data.rewardVault, this.network)
    );

    console.log(chalk.gray("\n" + "═".repeat(80)));
  }

  private formatStatus(status: string): string {
    switch (status.toLowerCase()) {
      case "created":
        return chalk.blue("🔵 Created");
      case "started":
        return chalk.green("🟢 Started");
      case "ended":
        return chalk.red("🔴 Ended");
      default:
        return chalk.white(`⚪ ${status}`);
    }
  }

  private formatCheckpointStatus(status: string): string {
    switch (status.toLowerCase()) {
      case "loadedprizes":
        return chalk.green("Loaded Prizes");
      case "processing":
        return chalk.yellow("Processing");
      case "finished":
        return chalk.green("Finished");
      default:
        return chalk.white(`${status}`);
    }
  }

  async execute(name: string): Promise<void> {
    if (!name) {
      console.error("Error: Name parameter is required");
      process.exit(400);
    }

    const crunchAccountService = CrunchAccountServiceWithContext({
      program: this.program,
    });

    try {
      const crunchService = this.getCrunchService();
      const crunch = await crunchService.getCrunch(name);

      // Get additional information
      const crunchAddress = crunchAccountService.getCrunchAddress(name);

      // Get current checkpoint if available
      let currentCheckpoint = null;
      let allCheckpoints = [];

      try {
        const checkpoint = await crunchAccountService.getCurrentCheckpoint(crunchAddress);
        if (checkpoint && checkpoint.account) {
          currentCheckpoint = {
            address: checkpoint.address.toBase58(),
            index: checkpoint.account.index,
            status: Object.keys(checkpoint.account.status)[0],
            timestamp: new Date(Number(checkpoint.account.timestamp) * 1000).toISOString(),
            prizesCount: checkpoint.account.prizes.length,
            totalPrizeAmount: checkpoint.account.prizes.reduce(
              (sum: number, prize: any) => sum + Number(prize.prize),
              0
            ),
          };
        }
      } catch {
        // Checkpoint doesn't exist or RPC error, which is fine
        console.warn("⚠️  Could not fetch current checkpoint (RPC may be limited)");
      }

      // Get checkpoints count (with fallback for RPC errors)
      try {
        allCheckpoints = await crunchAccountService.getCheckpointsForCrunch(crunchAddress);
      } catch {
        console.warn("⚠️  Could not fetch checkpoint history (RPC may be limited)");
        allCheckpoints = [];
      }

      // Get detailed participant information
      const participantsWithDetails = [];

      // First, get the crunch models to see if there are any models
      let crunchModels = null;
      let activeModelsCount = 0;
      try {
        crunchModels = await crunchAccountService.getCrunchModels(crunchAddress);
        activeModelsCount = crunchModels.account.models.length;
        console.warn(`⚠️  Found ${activeModelsCount} models in crunch`);
      } catch (e) {
        if (isAccountDoesNotExistError(e)) {
          activeModelsCount = 0;
        } else {
          console.warn("⚠️  Could not fetch crunch models");
          activeModelsCount = 0;
        }
      }

      // Fetch CrunchRegistrationState to get the current registration index
      let nextCruncherIndex: number | null = null;
      try {
        const [registrationStateAddress] = PublicKey.findProgramAddressSync(
          [Buffer.from("crunch_registration_state_2"), crunchAddress.toBuffer()],
          this.program.programId
        );
        const registrationState = await this.program.account.crunchRegistrationState.fetch(
          registrationStateAddress
        );
        nextCruncherIndex = registrationState.nextIndex;
      } catch (e) {
        // Registration state doesn't exist yet or RPC error
        if (!isAccountDoesNotExistError(e)) {
          console.warn("⚠️  Could not fetch crunch registration state");
        }
      }

      // Fetch claim records for this crunch to identify registered crunchers
      let claimRecords: any[] = [];
      try {
        claimRecords = await this.program.account.checkpointClaimRecord.all([
          {
            memcmp: {
              offset: 8 + 2, // discriminator + cruncher_index (u16)
              bytes: bs58.encode(crunchAddress.toBuffer()),
            },
          },
        ]);
      } catch (e) {
        console.warn("⚠️  Could not fetch checkpoint claim records for participants", e);
        claimRecords = [];
      }

      // Precompute map from claim record PDA to cruncher account info
      const claimRecordToCruncher = new Map<string, any>();
      let allCrunchers: Array<{ publicKey: PublicKey; account: any }> = [];
      try {
        allCrunchers = await this.program.account.cruncher.all();
        for (const cruncherAccount of allCrunchers) {
          const [claimRecordAddress] = PublicKey.findProgramAddressSync(
            [
              Buffer.from("payout_checkpoint_claim_record"),
              crunchAddress.toBuffer(),
              cruncherAccount.publicKey.toBuffer(),
            ],
            this.program.programId
          );
          claimRecordToCruncher.set(claimRecordAddress.toBase58(), cruncherAccount);
        }
      } catch (e) {
        console.warn("⚠️  Could not fetch cruncher accounts for participant lookup", e);
      }

      const sortedClaimRecords = claimRecords.sort(
        (a: any, b: any) => a.account.cruncherIndex - b.account.cruncherIndex
      );

      // Collect all model addresses to fetch in parallel
      const modelAddressesToFetch: PublicKey[] = [];
      const modelIndexMap = new Map<string, { cruncherIndex: number; desiredState: any }>();

      if (crunchModels) {
        for (const activeModel of crunchModels.account.models) {
          modelAddressesToFetch.push(activeModel.modelKey);
          modelIndexMap.set(activeModel.modelKey.toString(), {
            cruncherIndex: activeModel.cruncherIndex,
            desiredState: activeModel.desiredState,
          });
        }
      }

      // Batch fetch all model accounts in parallel
      const modelAccountsData = new Map<string, any>();
      if (modelAddressesToFetch.length > 0) {
        try {
          const accountsInfo = await this.program.provider.connection.getMultipleAccountsInfo(
            modelAddressesToFetch
          );

          for (let i = 0; i < modelAddressesToFetch.length; i++) {
            const accountInfo = accountsInfo[i];
            if (accountInfo && accountInfo.data) {
              try {
                const decoded = this.program.coder.accounts.decode("model", accountInfo.data);
                modelAccountsData.set(modelAddressesToFetch[i].toString(), decoded);
              } catch {
                // Failed to decode, will use fallback data
              }
            }
          }
        } catch (e) {
          console.warn("⚠️  Could not batch fetch model accounts", e);
        }
      }

      // Build participant details with pre-fetched model data
      for (const record of sortedClaimRecords) {
        const cruncherInfo = claimRecordToCruncher.get(record.publicKey.toBase58());
        const cruncherName = cruncherInfo?.account?.name || "Unknown";
        const cruncherAddress = cruncherInfo?.publicKey || record.publicKey;

        let modelDetails = [];
        let modelCount = 0;

        if (crunchModels) {
          try {
            const cruncherModels = crunchModels.account.models.filter(
              (m: any) => m.cruncherIndex === record.account.cruncherIndex
            );
            modelCount = cruncherModels.length;

            for (const activeModel of cruncherModels) {
              const modelKey = activeModel.modelKey.toString();
              const modelAccount = modelAccountsData.get(modelKey);

              if (modelAccount) {
                modelDetails.push({
                  id: modelAccount.id,
                  address: modelKey,
                  state: Object.keys(activeModel.desiredState)[0] || "unknown",
                  signature: modelAccount.signature,
                  submissionId: modelAccount.submissionId,
                  resourceId: modelAccount.resourceId,
                });
              } else {
                modelDetails.push({
                  id: "Unknown",
                  address: modelKey,
                  state: Object.keys(activeModel.desiredState)[0] || "unknown",
                  signature: "",
                  submissionId: "",
                  resourceId: "",
                });
              }
            }
          } catch {
            console.warn(`⚠️  Could not process models for cruncher ${cruncherAddress.toBase58()}`);
          }
        }

        participantsWithDetails.push({
          name: cruncherName,
          address: cruncherAddress.toString(),
          modelCount,
          models: modelDetails,
          cruncherIndex: record.account.cruncherIndex,
          claimRecordAddress: record.publicKey.toString(),
        });
      }

      // Format state for display
      const stateKey = Object.keys(crunch.state)[0];
      const stateDisplay = stateKey.charAt(0).toUpperCase() + stateKey.slice(1);

      // Fetch reward vault balance
      let rewardVaultBalance = 0;
      let rewardVaultDisplay = crunch.rewardVault.toBase58();
      try {
        const vaultAccount = await getAccount(this.program.provider.connection, crunch.rewardVault);
        rewardVaultBalance = Number(vaultAccount.amount);
        rewardVaultDisplay = `${crunch.rewardVault.toBase58()} (${formatMicroUsdcToUsdc(
          rewardVaultBalance
        )})`;
      } catch {
        // If we can't fetch the balance, just show the address
        rewardVaultDisplay = `${crunch.rewardVault.toBase58()} (Balance unavailable)`;
      }

      const enhancedOutput = {
        // Basic crunch information
        name: crunch.name,
        address: crunchAddress.toBase58(),
        state: stateDisplay,

        // Financial information
        payoutAmount: `${crunch.payoutAmount} USDC`,
        rewardVault: crunch.rewardVault.toBase58(),
        rewardVaultBalance: rewardVaultBalance,
        rewardVaultDisplay: rewardVaultDisplay,

        // Competition settings
        maxModelsPerCruncher: crunch.maxModelsPerCruncher,
        activeModelsCount: activeModelsCount,

        // Participant information
        participantCount: claimRecords.length,
        nextCruncherIndex: nextCruncherIndex,
        participants: claimRecords.map((record: any) => {
          const cruncherInfo = claimRecordToCruncher.get(record.publicKey.toBase58());
          return (cruncherInfo?.publicKey || record.publicKey).toBase58();
        }),
        participantsWithDetails: participantsWithDetails,

        // Checkpoint information
        checkpointsTotal: allCheckpoints.length,
        lastCheckpointIndex: crunch.lastCheckpointIndex,
        currentCheckpoint,

        // Raw data (for debugging/advanced users)
        raw: {
          name: crunch.name,
          rewardVault: crunch.rewardVault.toBase58(),
          state: crunch.state,
          lastCheckpointIndex: crunch.lastCheckpointIndex,
          maxModelsPerCruncher: crunch.maxModelsPerCruncher,
          payoutAmount: crunch.payoutAmount,
          nextCruncherIndex: nextCruncherIndex,
        },
      };

      // Output based on format preference
      if (this.resolvedOptions.output === "json") {
        this.output(enhancedOutput);
      } else {
        // Create beautiful table output
        this.displayCrunchInfo(enhancedOutput);
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("Account does not exist")) {
        console.error("Error: Crunch not found");
        process.exit(404);
      }
      throw error;
    }
  }
}
